import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Employee HR-admin API — list, detail, department-grouped view, and career
 * paths. Extends the existing employee-picker helpers (options, contains) used
 * across the app. All data comes from DataCentral v4 `/employees` routes.
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

/** Full employee record returned by both list and detail endpoints. */
export interface EmployeeRecord {
  employee_id: number;
  employee_name: string;
  employee_code: string | null;
  employee_email: string;
  employee_email_company: string;
  employee_phone_number: string | null;
  employee_birthday: string | null;
  employee_birthplace: string | null;
  employee_gender: string;
  employee_marriage: string;
  employee_nationality: string | null;
  employee_nation: string | null;
  employee_religion: string | null;
  employee_idno: string | null;
  employee_idno_issued_on: string | null;
  employee_idno_issued_by: string | null;
  employee_original_place: string | null;
  employee_country: string | null;
  employee_city: string | null;
  employee_district: string | null;
  employee_ward: string | null;
  employee_street: string | null;
  employee_temporary_city: string | null;
  employee_temporary_district: string | null;
  employee_temporary_ward: string | null;
  employee_temporary_street: string | null;
  employee_university: string | null;
  employee_major: string | null;
  employee_experience: string | null;
  employee_bank_name: string | null;
  employee_bank_branch: string | null;
  employee_bank_number: string | null;
  employee_personal_tax_code: string | null;
  employee_health_insurance: string | null;
  employee_social_insurance: string | null;
  employee_medical_registration_place: string | null;
  employee_status: number;
  employee_deleted_at: string | null;
  employee_start_date: string;
  employee_end_date: string | null;
  employee_confirmed: boolean;
  employee_keycloak_id: string;
  employee_contract_type: string;
  employee_seniority_level: string | null;
  employee_external_title: string | null;
  employee_avatar: string | null;
  employee_uuid: string;
  employee_day_off_left: number;
  is_collaborator: boolean;
  probation_date: string | null;
  employee_created_at: string;
  employee_updated_at: string | null;
  department_id: number;
  department_name: string;
  position_id: number;
  position_name: string;
  contact_homies?: ContactHomie[] | Record<string, never> | null;
  attendances?: Attendance[] | Record<string, never> | null;
}

export interface ContactHomie {
  contact_name: string;
  contact_phone: string;
  contact_relationship: string;
}

export interface Attendance {
  name: string;
  description: string;
}

/** Department-group entry from `GET /employees/departments`. */
export interface DepartmentGroup {
  department_id: number;
  department_name: string;
  department_code: string;
  department_description: string;
  department_status: string;
  department_leader_id?: number;
  department_leader_name?: string;
  employees?: EmployeeRecord[];
}

/** List query params for `GET /employees`. */
export interface EmployeeListParams {
  page?: number;
  page_size?: number;
  q?: string;
  employee_status?: number[];
  employee_contract_type?: string;
  employee_gender?: string;
  sort_by?: string;
  sort_order?: string;
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const employeeKeys = {
  list: (params: EmployeeListParams) => ['employees', 'list', params] as const,
  detail: (id: number) => ['employees', 'detail', id] as const,
  departments: () => ['employees', 'departments'] as const,
  careerPaths: (id: number) => ['employees', 'career-paths', id] as const,
  options: (q: string, pageSize: number) => ['employees', 'options', q, pageSize] as const,
} as const;

/* ── Hooks ───────────────────────────────────────────────────────────────── */

/** `GET /employees` — paginated employee list. */
export function useEmployeeList(params: EmployeeListParams) {
  return useQuery({
    queryKey: employeeKeys.list(params),
    queryFn: async (): Promise<ResponseArray<EmployeeRecord>> => {
      const queryObj: Record<string, unknown> = {
        page: params.page ?? 1,
        page_size: params.page_size ?? 20,
      };
      if (params.q) queryObj.q = params.q;
      if (params.employee_contract_type) queryObj.employee_contract_type = params.employee_contract_type;
      if (params.employee_gender) queryObj.employee_gender = params.employee_gender;
      if (params.employee_status?.length) {
        // API accepts employee_status as repeated param
        queryObj.employee_status = params.employee_status;
      }
      if (params.sort_by) queryObj.sort_by = params.sort_by;
      if (params.sort_order) queryObj.sort_order = params.sort_order;

      const res = await dataCentralApi.get<ResponseArray<EmployeeRecord>>(
        `/employees?${qs.stringify(queryObj, { arrayFormat: 'none' })}`,
      );
      return res.data;
    },
  });
}

/** `GET /employees/:id` — single employee detail. */
export function useEmployeeDetail(id: number | undefined) {
  return useQuery({
    queryKey: employeeKeys.detail(id!),
    enabled: !!id,
    queryFn: async (): Promise<EmployeeRecord> => {
      const res = await dataCentralApi.get<ResponseObject<EmployeeRecord>>(
        `/employees/${id}`,
      );
      return res.data.result;
    },
  });
}

/** `GET /employees/departments` — employees grouped by department. */
export function useEmployeesByDepartment() {
  return useQuery({
    queryKey: employeeKeys.departments(),
    queryFn: async (): Promise<DepartmentGroup[]> => {
      const res = await dataCentralApi.get<{ message: string; results: DepartmentGroup[] | null }>(
        '/employees/departments',
      );
      return res.data.results ?? [];
    },
  });
}

/* ── Employee option picker (already used across the app — re-exported) ─── */

export interface EmployeeOption {
  employee_id: number;
  employee_name: string;
}

/** `GET /employees/options?q=` — searchable picker list. */
export function useEmployeeOptions(query = '', pageSize = 50) {
  return useQuery({
    queryKey: employeeKeys.options(query, pageSize),
    queryFn: async (): Promise<EmployeeOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<EmployeeOption>>(
        `/employees/options?${qs.stringify({ q: query, page: 1, page_size: pageSize })}`,
      );
      return res.data.results ?? [];
    },
  });
}

/** `POST /employees/contains` — resolve ids → names. */
export function useEmployeesByIds(ids: number[]) {
  const sorted = [...ids].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['employees', 'contains', sorted],
    enabled: sorted.length > 0,
    queryFn: async (): Promise<EmployeeOption[]> => {
      const res = await dataCentralApi.post<ResponseArray<EmployeeOption>>('/employees/contains', {
        employee_ids: sorted,
      });
      return res.data.results ?? [];
    },
  });
}
